import { useFetch } from 'use-http';
import { useUserProfile } from '../enrollment/use-user-profile';
import { useMedicareQuote } from '../medicare/use-medicare-quote';
import { covertDateToSeverFormat } from '@coverright/utils';
import { getToken } from '@coverright/shared/keycloak';
import {
  BenefitPreference,
  DoctorInput,
  EnrollmentStatus,
  RecommendationsRequest,
  RecommendationsResponse,
  SpecialProgram,
} from './plan-engine-types';
import { Gender } from '@coverright/data-access/types/enrollment';
import { useAppContext } from '@coverright/shared/contexts';
import { environment } from '../../../../environments/environment';
import { MedicareQuoteOutput } from '@coverright/data-access/types/medicare';

export const usePlanEngine = (quote?: MedicareQuoteOutput | null) => {
  const profile = useUserProfile();
  const [{ hasDrugs, hasDoctors, benefits, programs, situation, paymentPreference, planType }] = useAppContext();
  const {post, response, loading, error} = useFetch(environment.planEngineAiUrl, {
    //suspense: true,
    interceptors: {
      request: async ({ options }) => {
        const token = await getToken();
        if (token) {
          options.headers = {
            ...options.headers,
            Authorization: `Bearer ${token}`,
          };
        }
        return options;
      },
    },
  })

  const recommendations = async (options: Partial<RecommendationsRequest> = {}): Promise<RecommendationsResponse | undefined> => {
    if (profile) {
      const input: RecommendationsRequest = {
        zip: quote?.zip as string,
        dob: covertDateToSeverFormat(profile.birthDate) as string,
        gender: profile.gender as Gender,
        tobacco: profile.tobacco ?? undefined,
        enrollment_status: situation as EnrollmentStatus ?? 'new_to_medicare',
        "cost_style": paymentPreference,
        "has_drugs": hasDrugs,
        "special_programs": (programs ?? []) as SpecialProgram[],
        "drugs": quote?.preferredDrugs.map(d => ({rxcui: d.packRxcui, name: d.packName})),
        "benefit_preferences": benefits as BenefitPreference[] ?? [],
        "has_doctors": hasDoctors,
        "doctors": quote?.preferredDoctors.map(d => ({
          name: d.name,
          npi: d.npi,
          zip: d.addresses?.at(0)?.addressLine2?.match(/\d{5}/)?.[0],
          specialty: d.addresses?.at(0)?.specialty,
        }) as DoctorInput),
        "top_n": 3,
        ...options,
      };

      const res = await post('/recommendations', input);

      if (response.ok) return res;
    }
  };

  const loaded = !!profile && !!quote;

  return {
    loaded,
    loading,
    recommendations
  }
}
